import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { modulesAPI, questionsAPI, progressAPI } from "../services/api";
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  Tabs, Tab, Chip, Alert, CircularProgress, LinearProgress
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArticleIcon from "@mui/icons-material/Article";
import QuizIcon from "@mui/icons-material/Quiz";
import TimerIcon from "@mui/icons-material/Timer";
import LockIcon from "@mui/icons-material/Lock";

export default function ModulePage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [module, setModule] = useState(null);
  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoProgress, setVideoProgress] = useState(0);
  const [transcriptRead, setTranscriptRead] = useState(false);
  const [mediaCompleted, setMediaCompleted] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [cooldowns, setCooldowns] = useState({});

  useEffect(() => { loadModule(); }, [moduleId]);

  useEffect(() => {
    if (templates.length === 0) return;
    const loadCooldowns = async () => {
      const cooldownMap = {};
      for (const t of templates) {
        try {
          const cdRes = await questionsAPI.checkCooldown(t.template_id);
          cooldownMap[t.template_id] = cdRes.data;
        } catch (e) {
          console.error("Cooldown check failed for", t.template_id, e);
        }
      }
      setCooldowns(cooldownMap);
    };
    loadCooldowns();
  }, [templates]);

  const loadModule = async () => {
    try {
      const [modRes, templatesRes] = await Promise.all([
        modulesAPI.getModule(moduleId),
        questionsAPI.getTemplates(moduleId)
      ]);
      setModule(modRes.data);
      setTemplates(templatesRes.data);
      // Load existing progress
      try {
        const progRes = await progressAPI.getModuleProgress(moduleId);
        if (progRes.data) {
          setVideoProgress(progRes.data.video_percent || 0);
          setMediaCompleted(progRes.data.video_completed || false);
          setTranscriptRead(progRes.data.transcript_viewed || false);
        }
      } catch {}
    } catch {
      setMessage("Failed to load module.");
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  const handleVideoTimeUpdate = async () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const percent = Math.round((video.currentTime / video.duration) * 100);
    setVideoProgress(percent);
    if (percent > 0 && percent % 10 === 0) {
      try {
        await progressAPI.trackVideo(moduleId, percent, 10);
      } catch {}
    }
  };

  const handleVideoEnded = async () => {
    setVideoProgress(100);
    setMediaCompleted(true);
    setMediaCompleted(true);
    try {
      await progressAPI.trackVideo(moduleId, 100, 10);
      const mediaType = module?.video?.media_type || "video";
      const mediaLabel = mediaType === "audio" ? "Audio" : mediaType === "pdf" ? "Document" : mediaType === "text" ? "Document" : "Video";
      setMessage(`${mediaLabel} completed! Now read the transcript and take the exam.`);
      setMessageType("success");
    } catch {}
  };

  const handleMarkTranscript = async () => {
    try {
      await progressAPI.markTranscript(moduleId);
      setTranscriptRead(true);
      setMessage("Transcript marked as read. You can now take the exam!");
      setMessageType("success");
    } catch {
      setMessage("Failed to mark transcript.");
      setMessageType("error");
    }
  };

  const handleStartExam = (templateId) => {
    navigate(`/exam/${templateId}`);
  };

  const formatCountdown = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  // Live countdown ticker
  useEffect(() => {
    const timer = setInterval(() => {
      setCooldowns(prev => {
        const updated = {...prev};
        for (const id in updated) {
          if (updated[id].in_cooldown && updated[id].seconds_remaining > 0) {
            updated[id] = {...updated[id], seconds_remaining: updated[id].seconds_remaining - 1};
            if (updated[id].seconds_remaining <= 0) {
              updated[id] = {...updated[id], in_cooldown: false};
            }
          }
        }
        return updated;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <AppBar position="static" sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
        <Toolbar>
          <img src="/alteram-logo.png" alt="Alteram" style={{ height: 36, objectFit: "contain", marginRight: 8, backgroundColor: "white", padding: "3px 6px", borderRadius: 4 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Alteram Contact Centre Training
          </Typography>
          <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 4, maxWidth: 1000, mx: "auto" }}>
        {message && (
          <Alert severity={messageType} sx={{ mb: 2 }} onClose={() => setMessage("")}>
            {message}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700} color="#1a237e">
            {module?.module_title}
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            {module?.description}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Chip label={`Pass mark: ${module?.pass_threshold}%`} size="small" color="primary" />
            <Chip label={`Max retakes: ${module?.max_retakes}`} size="small" />
            <Chip label={module?.status} size="small"
              color={module?.status === "active" ? "success" : "default"} />
          </Box>
        </Box>

        <Tabs value={tab} onChange={(e, v) => setTab(v)}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
          <Tab icon={<PlayArrowIcon />} label={module?.video?.media_type === "pdf" ? "Document" : module?.video?.media_type === "audio" ? "Audio" : module?.video?.media_type === "text" ? "Text" : "Video"} iconPosition="start" />
          <Tab icon={<ArticleIcon />} label="Transcript" iconPosition="start" />
          <Tab icon={<QuizIcon />} label={!mediaCompleted ? "Assessment (Complete media first)" : !transcriptRead ? "Assessment (Read transcript first)" : "Assessment"} iconPosition="start" disabled={!mediaCompleted || !transcriptRead} />
        </Tabs>

        {tab === 0 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                {module?.video?.video_title || "Training Video"}
              </Typography>

              {module?.video ? (
                <Box>
                  {module.video.media_type === "pdf" ? (
                    <iframe src={module.video.storage_url} width="100%" height="600px"
                      title={module.video.video_title} style={{ border: "none", borderRadius: 8 }} />
                  ) : module.video.media_type === "text" ? (
                    <Box sx={{ p: 4, textAlign: "center", bgcolor: "#f5f7fa", borderRadius: 2 }}>
                      <Typography variant="h2" mb={2}>📝</Typography>
                      <Typography variant="h6" fontWeight={600} mb={1}>{module.video.video_title}</Typography>
                      <Typography variant="body2" color="text.secondary" mb={3}>Click the button below to download and open this document. Once you have read it, click Mark as Read to unlock the assessment.</Typography>
                      <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                        <Button variant="contained" href={module.video.storage_url} download
                          onClick={() => { setTimeout(() => handleVideoEnded(), 1000); }}
                          sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
                          Download Document
                        </Button>
                        {!mediaCompleted && (
                          <Button variant="outlined" onClick={handleVideoEnded}>
                            Mark Document as Read
                          </Button>
                        )}
                        {mediaCompleted && (
                          <Button variant="contained" disabled sx={{ bgcolor: "success.main" }}>
                            ✓ Document Completed
                          </Button>
                        )}
                      </Box>
                    </Box>
                  ) : module.video.media_type === "audio" ? (
                    <audio controls style={{ width: "100%", borderRadius: 8 }} ref={videoRef}
                      onTimeUpdate={handleVideoTimeUpdate} onEnded={handleVideoEnded}>
                      <source src={module.video.storage_url} type={module.video.storage_url?.endsWith('.mp3') ? 'audio/mpeg' : module.video.storage_url?.endsWith('.wav') ? 'audio/wav' : module.video.storage_url?.endsWith('.ogg') ? 'audio/ogg' : 'audio/mpeg'} />
                      Your browser does not support the audio tag.
                    </audio>
                  ) : (
                    <video ref={videoRef} controls
                      style={{ width: "100%", borderRadius: 8, maxHeight: 480, backgroundColor: "#000" }}
                      onTimeUpdate={handleVideoTimeUpdate} onEnded={handleVideoEnded}>
                      <source src={module.video.storage_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  )}

                  {videoProgress > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Watch progress
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {videoProgress}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={videoProgress}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  )}

                  {module?.video?.duration_seconds && (
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Duration: {Math.round(module.video.duration_seconds / 60)} minutes
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{
                  bgcolor: "#000", borderRadius: 2, height: 300,
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <Box sx={{ textAlign: "center", color: "white" }}>
                    <PlayArrowIcon sx={{ fontSize: 80, opacity: 0.3 }} />
                    <Typography variant="body1" sx={{ opacity: 0.5 }}>
                      No video uploaded yet
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 1 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Module Transcript
              </Typography>
              {module?.transcript ? (
                <>
                  <Box sx={{
                    bgcolor: "#f9f9f9", borderRadius: 2, p: 3,
                    border: "1px solid #e0e0e0", mb: 3, lineHeight: 1.8
                  }}>
                    <Typography variant="body1">
                      {module.transcript.content}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleMarkTranscript}
                    disabled={transcriptRead}
                  >
                    {transcriptRead ? "✓ Transcript Read" : "Mark as Read"}
                  </Button>
                </>
              ) : (
                <Typography color="text.secondary">
                  No transcript available yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {tab === 2 && (
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Available Assessments
              </Typography>
              {templates.length === 0 ? (
                <Typography color="text.secondary">
                  No assessments available for this module yet.
                </Typography>
              ) : (
                templates.map(template => (
                  <Card key={template.template_id} variant="outlined"
                    sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {template.template_name}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                            <Chip label={template.assessment_type} size="small" color="primary" />
                            <Chip label={`${template.question_count} questions`} size="small" />
                            <Chip label={`Pass: ${template.pass_mark}%`} size="small" color="success" />
                            {template.time_limit_minutes && (
                              <Chip label={`${template.time_limit_minutes} min`} size="small" />
                            )}
                          </Box>
                        </Box>
                        {cooldowns[template.template_id]?.max_attempts_reached ? (
                          <Box sx={{ textAlign: "center" }}>
                            <Chip icon={<LockIcon />} label="Max attempts reached" color="error" />
                            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                              {cooldowns[template.template_id].attempts_used}/{cooldowns[template.template_id].max_attempts} attempts used
                            </Typography>
                          </Box>
                        ) : cooldowns[template.template_id]?.in_cooldown ? (
                          <Box sx={{ textAlign: "center" }}>
                            <Chip icon={<TimerIcon />}
                              label={`Retake in: ${formatCountdown(cooldowns[template.template_id].seconds_remaining)}`}
                              color="warning" sx={{ fontWeight: 700, fontSize: 14, px: 1 }} />
                            <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                              {cooldowns[template.template_id].attempts_used}/{cooldowns[template.template_id].max_attempts} attempts used
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: "center" }}>
                            <Button
                              variant="contained"
                              size="large"
                              onClick={() => handleStartExam(template.template_id)}
                              sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}
                            >
                              Start Exam
                            </Button>
                            {cooldowns[template.template_id]?.attempts_used > 0 && (
                              <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                                {cooldowns[template.template_id].attempts_used}/{cooldowns[template.template_id].max_attempts} attempts used
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
}
